import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { buildUrl, isDefined } from '@trimble-gcs/common';
import { forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import { AppRoute } from '../../../app-route';
import { AppState } from '../../../app-state/app.state';
import { ScandataService } from '../../../scandata/scandata.service';
import { ALLOWED_EXTENSIONS, ImportFile } from '../../import.models';
import { allowImportForFileType } from '../../import.service';
import { PickerType } from '../file-picker.models';

declare const OneDrive: any;

interface PickerResult {
  value: PickerFile[];
}

interface PickerFile {
  '@microsoft.graph.downloadUrl': string;
  '@odata.context': string;
  id: string;
  name: string;
  size: number;
  file?: {
    hashes?: {
      quickXorHash?: string;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class OneDriveFilePickerService {
  private readonly store = inject(Store);
  private readonly scandataService = inject(ScandataService);

  show(): Observable<ImportFile[]> {
    return from(this.openPicker()).pipe(
      switchMap((files) => {
        if (files.length === 0) return of([]);

        const files$ = files.map((file) => this.mapToImportFile(file));
        return forkJoin(files$);
      }),
    );
  }

  private openPicker(): Promise<PickerFile[]> {
    const settings = this.getSettings();

    // https://github.com/OneDrive/onedrive-api-docs/blob/live/docs/controls/file-pickers/js-v72/open-file.md
    return new Promise((resolve, reject) => {
      const odOptions = {
        clientId: settings.clientId,
        action: 'download',
        multiSelect: true,
        advanced: {
          redirectUri: buildUrl(settings.redirectUri, AppRoute.OneDriveAuth),
          filter: 'folder,' + ALLOWED_EXTENSIONS.join(','),
          queryParameters: 'select=id,name,size,file',
        },
        success: (result: PickerResult) => {
          const files = result.value?.filter((file) => allowImportForFileType(file.name));
          resolve(files);
        },
        cancel: () => resolve([]),
        error: (error: any) => reject(error),
      };

      OneDrive.open(odOptions);
    });
  }

  private getSettings() {
    const appSettings = this.store.selectSnapshot(AppState.settings);
    return {
      clientId: appSettings.oneDrive.clientId,
      redirectUri: appSettings.baseUrl,
    };
  }

  private mapToImportFile(file: PickerFile): Observable<ImportFile> {
    const quickXorHash = file.file?.hashes?.quickXorHash;
    const externalFileId = isDefined(quickXorHash)
      ? `oneDrive-${file.id}-${quickXorHash}`
      : `oneDrive-${file.id}`;

    const alreadyIngested$ = this.scandataService
      .getScansByQuery({ filters: { externalFileId } })
      .pipe(map((scans) => scans.length > 0));

    return alreadyIngested$.pipe(
      map((alreadyIngested) => ({
        id: file.id,
        name: file.name,
        size: file.size,
        pickerType: PickerType.OneDrive,
        downloadUrl: file['@microsoft.graph.downloadUrl'],
        externalFileId,
        alreadyIngested,
      })),
    );
  }
}
